import React from "react";
import { Button } from "./ui/button";
import { RefreshCw, AlertTriangle, ArrowLeft } from "lucide-react";
import { API_BASE } from "../lib/apiBase";
import { trHorsContexte as tr } from "../lib/langHorsContexte";

/**
 * Vincent P2.c (Eric 2026-07-31 SPEC STRICK PROD) — Boundary d'erreur
 * INTERNE au DashboardLayout.
 *
 * Différence avec `ErrorBoundary` (racine, full-screen) :
 *   - Ne remplace QUE le contenu de la route active (le sidebar / topbar /
 *     navigation restent visibles → l'utilisateur peut cliquer une autre
 *     page pour continuer à travailler).
 *   - Reset automatique quand `resetKey` change (typiquement
 *     `location.pathname` → auto-reset dès qu'on navigue ailleurs).
 *
 * Reporting backend identique à ErrorBoundary racine (endpoint
 * `/api/client-errors` best-effort, avec `boundary: "route"` pour
 * distinguer dans les diagnostics admin).
 */
export default class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null, errorId: null, lastResetKey: props.resetKey };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  static getDerivedStateFromProps(props, state) {
    // Vincent P2.c — Reset auto sur navigation. Quand `resetKey` (typiquement
    // location.pathname) change, on nettoie l'état d'erreur pour laisser la
    // nouvelle route se rendre normalement.
    if (props.resetKey !== state.lastResetKey) {
      return { error: null, errorInfo: null, errorId: null, lastResetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error, errorInfo) {
    const errorId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    this.setState({ error, errorInfo, errorId });
    try {
      const url = API_BASE || "";
      fetch(`${url}/api/client-errors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: error?.message || String(error),
          stack: error?.stack || "",
          component_stack: errorInfo?.componentStack || "",
          url: window.location.href,
          user_agent: navigator.userAgent,
          error_id: errorId,
          boundary: "route",
          route: this.props.resetKey || "",
          ts: new Date().toISOString(),
        }),
        credentials: "include",
        keepalive: true,
      }).catch(() => {});
    } catch (_) { /* ignore */ }
    console.error("[RouteErrorBoundary]", error, errorInfo);
  }

  handleReload = () => window.location.reload();
  handleGoBack = () => window.history.length > 1 ? window.history.back() : (window.location.href = "/dashboard");

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        data-testid="route-error-boundary-fallback"
        className="p-8 max-w-3xl mx-auto"
      >
        <div className="rounded-xl border border-red-200 bg-red-50/60 p-6">
          <div className="flex items-start gap-4 mb-4">
            <AlertTriangle className="w-10 h-10 text-red-600 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-xl font-semibold text-red-900 mb-1">
                {tr("Cette page a rencontré une erreur", "This page hit an error")}
              </h2>
              <p className="text-sm text-red-700/90">
                {tr(
                  "Vous pouvez continuer à utiliser Knoria — utilisez la navigation à gauche pour changer de page, ou rechargez cette section.",
                  "You can keep using Knoria — use the navigation on the left to switch page, or reload this section.",
                )}
              </p>
            </div>
          </div>

          <div className="bg-white/70 border border-red-100 rounded-lg p-3 mb-4 font-mono text-xs">
            <div className="text-red-800/70 mb-0.5">{tr("ID d\u2019erreur", "Error ID")}</div>
            <div data-testid="route-error-boundary-id" className="text-red-900 mb-2">
              {this.state.errorId}
            </div>
            <div className="text-red-800/70 mb-0.5">{tr("Message", "Message")}</div>
            <div className="text-red-900 break-words">
              {this.state.error?.message || String(this.state.error)}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="route-error-reload-btn"
              onClick={this.handleReload}
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              {tr("Recharger la page", "Reload the page")}
            </Button>
            <Button
              data-testid="route-error-back-btn"
              onClick={this.handleGoBack}
              size="sm"
              variant="outline"
              className="border-red-200 text-red-800 hover:bg-red-100"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />
              {tr("Retour", "Back")}
            </Button>
            <a
              href={`mailto:support@knoria.com?subject=Erreur%20${encodeURIComponent(this.state.errorId)}`}
              className="inline-flex"
            >
              <Button
                size="sm"
                variant="outline"
                className="border-red-200 text-red-800 hover:bg-red-100"
              >
                {tr("Contacter le support", "Contact support")}
              </Button>
            </a>
          </div>
        </div>
      </div>
    );
  }
}
